import { useState } from 'react';
import { useMessages } from '../i18n/context';
import type { ComponentResponse } from '../api/types';
import type { NodeCallbacks } from '../ComponentNode';
import { isDone, tasksOf, titleOf } from './todo';
import { blockTypeName } from './blockTypeName';

/** The to-do list's own actions — each one wire operation upstairs (ADR-029 §3). */
export interface TodoCallbacks {
  onAddTask: (listId: string, title: string) => void;
  onToggle: (taskId: string, done: boolean) => void;
  onRenameTask: (taskId: string, title: string) => void;
  /** `index` is the target position among the list's children (the server's coordinate). */
  onMoveTask: (listId: string, taskId: string, index: number) => void;
  onRemoveTask: (taskId: string) => void;
}

type Mode =
  | {
      editable: true;
      /** The list's position among its siblings, for the list-level move. */
      index: number;
      siblingCount: number;
      parentId?: string;
      /** The list-level actions (rename, move, remove) — the same as any block's. */
      callbacks: NodeCallbacks;
      todo: TodoCallbacks;
    }
  | { editable: false };

/**
 * The first planning component (ADR-029 §3): a `todo-list` block rendered as a checklist —
 * its tasks in array order with a checkbox each, add / rename / move / remove at ≥ Write,
 * plain content below it. Every action posts one operation and the block re-renders from the
 * server's document (ADR-025); nothing is ticked locally. While a request is in flight the
 * text input and the checkboxes stay enabled (disabling the focused control would blur it and
 * close a phone's keyboard) — the block just ignores a second toggle and holds the buttons.
 */
export function TodoList(props: { list: ComponentResponse; busy: boolean } & Mode) {
  const { list, busy } = props;
  const t = useMessages();
  const g = t.grove;
  const TODO = t.todo;
  const [renaming, setRenaming] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [newTask, setNewTask] = useState('');
  const title = titleOf(list);
  const tasks = tasksOf(list);
  const trimmedTask = newTask.trim();
  const trimmedDraft = draft.trim();

  const startRename = (id: string, current: string) => {
    setDraft(current);
    setRenaming(id);
  };

  const renameForm = (id: string, label: string, save: (value: string) => void) =>
    renaming === id ? (
      <form
        className="inline-form"
        aria-label={t.node.renameButton}
        onSubmit={(e) => {
          e.preventDefault();
          if (trimmedDraft === '' || busy) return;
          setRenaming(null);
          save(trimmedDraft);
        }}
      >
        <input aria-label={label} value={draft} onChange={(e) => setDraft(e.target.value)} />
        <button className="button-primary" type="submit" disabled={busy || trimmedDraft === ''}>
          {TODO.save}
        </button>
        <button className="button-quiet" type="button" onClick={() => setRenaming(null)}>
          {TODO.cancel}
        </button>
      </form>
    ) : null;

  return (
    <li className="todo-list card" data-block="todo-list">
      <section aria-label={TODO.list(title)} aria-busy={busy}>
        <div className="todo-header">
          <div className="block-title">
            <h3>{title}</h3>
            <span className="block-type">({blockTypeName(t, list.type)})</span>
            <span className="task-count">{g.listComplete(tasks.filter(isDone).length, tasks.length)}</span>
          </div>
          {props.editable && (
            <div className="block-actions" role="group" aria-label={g.actionsFor(title)}>
              <button
                className="button-quiet"
                aria-label={TODO.listMoveUp(title)}
                disabled={busy || props.index === 0}
                onClick={() => props.callbacks.onMove(list.id, props.index - 1, props.parentId)}
              >
                {t.node.up}
              </button>
              <button
                className="button-quiet"
                aria-label={TODO.listMoveDown(title)}
                disabled={busy || props.index === props.siblingCount - 1}
                onClick={() => props.callbacks.onMove(list.id, props.index + 1, props.parentId)}
              >
                {t.node.down}
              </button>
              <button
                className="button-quiet"
                aria-label={TODO.listRename(title)}
                disabled={busy}
                onClick={() => startRename(list.id, title)}
              >
                {t.node.renameButton}
              </button>
              <button
                className="button-danger"
                aria-label={TODO.listRemove(title)}
                disabled={busy}
                onClick={() => props.callbacks.onRemove(list.id)}
              >
                {t.node.removeButton}
              </button>
            </div>
          )}
        </div>
        {props.editable &&
          renameForm(list.id, TODO.listNewTitle(title), (value) =>
            props.callbacks.onRename(list.id, value),
          )}

        {tasks.length === 0 ? (
          <p className="empty-state">{TODO.empty}</p>
        ) : (
          <ul className="task-list">
            {tasks.map((task, i) => {
              const taskTitle = titleOf(task);
              const done = isDone(task);
              // Moves are among tasks, in the list's child coordinates: past the neighbouring
              // task, whatever other blocks sit between them.
              const previous = tasks[i - 1];
              const next = tasks[i + 1];
              return (
                <li className="task-row" key={task.id} data-done={done ? 'true' : 'false'}>
                  <label className="task-check">
                    {props.editable ? (
                      <input
                        type="checkbox"
                        aria-label={TODO.done(taskTitle)}
                        checked={done}
                        onChange={(e) => {
                          if (!busy) props.todo.onToggle(task.id, e.target.checked);
                        }}
                      />
                    ) : (
                      // Read-only: the tick IS the content, so it keeps the normal palette and
                      // the tab order (a disabled control is painted greyed out) — controlled to
                      // the document with no handler, a click changes nothing.
                      <input
                        type="checkbox"
                        aria-label={TODO.done(taskTitle)}
                        aria-readonly="true"
                        checked={done}
                        onChange={() => undefined}
                      />
                    )}{' '}
                    <span className="task-title">{taskTitle}</span>
                  </label>
                  {props.editable && (
                    <>
                      <div className="task-actions" role="group" aria-label={g.actionsFor(taskTitle)}>
                        <button
                          className="button-quiet"
                          aria-label={TODO.moveUp(taskTitle)}
                          disabled={busy || previous === undefined}
                          onClick={() =>
                            props.todo.onMoveTask(list.id, task.id, list.children.indexOf(previous!))
                          }
                        >
                          {t.node.up}
                        </button>
                        <button
                          className="button-quiet"
                          aria-label={TODO.moveDown(taskTitle)}
                          disabled={busy || next === undefined}
                          onClick={() =>
                            props.todo.onMoveTask(list.id, task.id, list.children.indexOf(next!))
                          }
                        >
                          {t.node.down}
                        </button>
                        <button
                          className="button-quiet"
                          aria-label={TODO.rename(taskTitle)}
                          disabled={busy}
                          onClick={() => startRename(task.id, taskTitle)}
                        >
                          {t.node.renameButton}
                        </button>
                        <button
                          className="button-danger"
                          aria-label={TODO.remove(taskTitle)}
                          disabled={busy}
                          onClick={() => props.todo.onRemoveTask(task.id)}
                        >
                          {t.node.removeButton}
                        </button>
                      </div>
                      {renameForm(task.id, TODO.newTitle(taskTitle), (value) =>
                        props.todo.onRenameTask(task.id, value),
                      )}
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {props.editable && (
          <form
            className="add-form"
            aria-label={g.addTaskTo(title)}
            onSubmit={(e) => {
              e.preventDefault();
              if (trimmedTask !== '' && !busy) {
                props.todo.onAddTask(list.id, trimmedTask);
                setNewTask('');
              }
            }}
          >
            <input
              aria-label={TODO.newTask}
              value={newTask}
              onChange={(e) => setNewTask(e.target.value)}
              placeholder={TODO.addPlaceholder}
            />
            <button className="button-secondary" type="submit" disabled={busy || trimmedTask === ''}>
              {TODO.addTask}
            </button>
          </form>
        )}
      </section>
    </li>
  );
}
